import { getImageUrl } from "../admin/api/masterApi";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.trivomtara.com/api"
).replace(/\/$/, "");

async function fetchJson(path, options = {}, fallbackMessage = "Request failed") {
  const res = await fetch(`${API_BASE_URL}${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || fallbackMessage);
  }
  return data.data;
}

function isActiveStatus(status) {
  if (status === undefined || status === null) return true;
  if (typeof status === "boolean") return status;
  if (typeof status === "string") {
    const normalized = status.trim().toLowerCase();
    return (
      normalized === "active" ||
      normalized === "yes" ||
      normalized === "1" ||
      normalized === "true"
    );
  }
  return Number(status) === 1;
}

export function isCityLocation(location) {
  const typeName = String(location?.location_type_name || "").toLowerCase();
  const category = String(location?.location_category || "").toLowerCase();
  return typeName.includes("city") || category.includes("city");
}

export function resolveLocationCountry(locations, locationId) {
  const byId = new Map(
    (locations || []).map((location) => [String(location.location_id), location])
  );
  let current = byId.get(String(locationId));
  let countryCode = "";
  let guard = 0;

  while (current && guard < 20) {
    if (!countryCode && current.country_code) {
      countryCode = String(current.country_code);
    }
    const typeName = String(current.location_type_name || "").toLowerCase();
    if (typeName.includes("country") && current.country_code) {
      countryCode = String(current.country_code);
      break;
    }
    if (current.parent_location_id == null) break;
    current = byId.get(String(current.parent_location_id));
    guard += 1;
  }

  return { country_code: countryCode || "+91" };
}

export async function listUserRoles() {
  return fetchJson("/user-roles", {}, "Failed to fetch user roles");
}

export async function getBuyerRoleId() {
  const roles = await listUserRoles();
  const active = (roles || []).filter((role) => isActiveStatus(role.status));
  const buyer =
    active.find(
      (role) => String(role.user_role_code || "").toLowerCase() === "buyer"
    ) ||
    active.find(
      (role) => String(role.user_role || "").toLowerCase() === "buyer"
    ) ||
    active.find(
      (role) => String(role.user_role_code || "").toLowerCase() === "customer"
    ) ||
    active.find((role) =>
      String(role.user_role || "")
        .toLowerCase()
        .includes("customer")
    );

  if (!buyer) {
    throw new Error("Buyer role is not configured on the server");
  }
  return buyer.user_role_id;
}

export async function listLocations() {
  return fetchJson("/locations", {}, "Failed to fetch locations");
}

export async function loginWithPhone(phone, password) {
  return fetchJson(
    "/auth/login",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, password }),
    },
    "Invalid credentials"
  );
}

export async function createUserLogin(payload) {
  return fetchJson(
    "/user-logins",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    "Failed to create login"
  );
}

/**
 * Same flow as mobile signupBuyer:
 * create user_master with buyer role → create login → auto login.
 */
export async function signupBuyer(payload) {
  const userRoleId = await getBuyerRoleId();
  const formData = new FormData();

  formData.append("user_role_id", String(userRoleId));
  formData.append("first_name", payload.firstName.trim());
  formData.append("last_name", payload.lastName.trim());
  formData.append("gender", payload.gender.trim());
  formData.append("age", String(payload.age).trim());
  formData.append("location_id", String(payload.locationId));
  formData.append("country_code", (payload.countryCode || "+91").trim());
  formData.append(
    "phone_numbers",
    JSON.stringify(
      payload.phoneNumbers.map((phone) => phone.trim()).filter(Boolean)
    )
  );
  formData.append("status", "Active");

  if (payload.profilePicture instanceof File) {
    formData.append("profile_picture", payload.profilePicture);
  }

  const user = await fetchJson(
    "/users",
    { method: "POST", body: formData },
    "Failed to create account"
  );

  const primaryPhone = payload.phoneNumbers
    .map((phone) => phone.trim())
    .filter(Boolean)[0];
  const fullName = [payload.firstName, payload.lastName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");

  await createUserLogin({
    user_role_id: userRoleId,
    reference_id: user.user_id,
    name: fullName || payload.firstName,
    phone: primaryPhone,
    password: payload.password,
    must_change_password: 0,
    status: 1,
  });

  const login = await loginWithPhone(primaryPhone, payload.password);
  return { user, login };
}

export { getImageUrl, API_BASE_URL };
